import ISO6391 from 'iso-639-1';
import { useEffect, useMemo } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType, object, string } from 'yup';

import { CreatePersonModel, handleBackendValidationErrors, PersonReadModel, UpdatePersonModel } from '@client/api';
import {
  availableLanguages,
  Button,
  cultureValueSchema,
  FormField,
  FormSubmitButton,
  getCountries,
  GridCol,
  GridRow,
  Input,
  invoicingIntegrationExternalReferenceValueSchema,
  LanguageCode,
  Modal,
  ModalContent,
  ModalFooter,
  personSourceExternalReferenceValueSchema,
  Select,
  socialSecurityNumberValueSchema,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';
import { timeZonesNames } from '@vvo/tzdb';
import { useSession } from '../../../hooks';

type FormModel = CreatePersonModel | UpdatePersonModel;

const schema = object({
  firstName: string().required(),
  lastName: string().required(),
  email: string().email().required(),
  socialSecurityNumber: socialSecurityNumberValueSchema.nullable().makeOptional(),
  culture: cultureValueSchema,
  vatId: string().nullable(),
  invoicingAccount: invoicingIntegrationExternalReferenceValueSchema.nullable().makeOptional(),
  source: personSourceExternalReferenceValueSchema.nullable().makeOptional(),
});

type FormValues = InferType<typeof schema>;
type FormPath = FieldPath<FormValues>;
type FormDefaultValues = DeepPartial<FormValues>;

export type PersonFormData = FormValues;

const INVOICING_INTEGRATIONS = ['Stripe'];

const ALLOWED_LANGUAGE_CODES: LanguageCode[] = availableLanguages.map((x) => x.code);
// sort by name
const LANGUAGES = ISO6391.getAllCodes()
  .map((x) => ({ code: x, name: ISO6391.getName(x) }))
  .filter((x) => ALLOWED_LANGUAGE_CODES.includes(x.code))
  .sort((a, b) => (a.name > b.name ? 0 : -1));

interface UserFormProps {
  title: React.ReactNode;
  onSubmit: (data: FormValues) => Promise<void>;
  onClose: () => void;
  person?: PersonReadModel;
  open?: boolean;
  edit?: boolean;
  hideAdvancedFields?: boolean;
}

export function UserForm({ open = true, edit = false, hideAdvancedFields = false, title, onSubmit, onClose, person }: UserFormProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const { sessionPerson } = useSession();

  const sessionPersonLanguage = useMemo(() => sessionPerson?.culture?.language, [sessionPerson?.culture?.language]);
  const sessionPersonTimezone = useMemo(() => sessionPerson?.culture?.timezone, [sessionPerson?.culture?.timezone]);

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      firstName: person?.firstName ?? '',
      lastName: person?.lastName ?? '',
      email: person?.email ?? '',
      culture: {
        language: edit ? person?.culture?.language ?? '' : sessionPersonLanguage ?? '',
        timezone: edit ? person?.culture?.timezone ?? '' : sessionPersonTimezone ?? '',
      },
      vatId: person?.vatId ?? '',
      socialSecurityNumber: {
        country: {
          threeLetterCode: person?.socialSecurityNumber?.country?.threeLetterCode ?? '',
        },
        value: person?.socialSecurityNumber?.value,
      },
      source: {
        identifier: person?.source?.identifier ?? '',
        integration: person?.source?.integration ?? ('' as any),
      },
    }),
    [
      edit,
      person?.culture?.language,
      person?.culture?.timezone,
      person?.email,
      person?.firstName,
      person?.lastName,
      person?.socialSecurityNumber?.country?.threeLetterCode,
      person?.socialSecurityNumber?.value,
      person?.source?.identifier,
      person?.source?.integration,
      person?.vatId,
      sessionPersonLanguage,
      sessionPersonTimezone,
    ],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { handleSubmit, reset, control, setError } = methods;

  const handleFormSubmit = async (data: FormValues) => {
    await onSubmit(data).catch((error) => {
      handleBackendValidationErrors(
        {
          Name: 'firstName',
          Email: 'email',
          LastName: 'lastName',
          Culture: 'culture',
          VatId: 'vatId',
          SocialSecurityNumber: 'socialSecurityNumber',
          Source: 'source.integration',
          SourceIdentifier: 'source.identifier',
        },
        error,
        (name, error) => setError(name as FormPath, error),
      );
    });
  };

  const formCultureLanguage = useWatch({ control, name: 'culture.language' });
  const currentLanguage = useMemo(
    () => formCultureLanguage || person?.culture?.language || sessionPersonLanguage || '',
    [formCultureLanguage, person?.culture?.language, sessionPersonLanguage],
  );
  const countriesSorted = useMemo(
    () => getCountries().sort((x) => (x.alpha2.toLowerCase() === currentLanguage.toLowerCase() ? -1 : 1)),
    [currentLanguage],
  );

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal open={open} size="lg" onClose={onClose} title={title}>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalContent className="space-y-8 p-4">
            <article>
              <GridRow cols={{ def: 1, sm: 6 }} spacingX={2} spacingY={4}>
                <GridCol span={{ def: 1, sm: 6 }}>
                  <FormField
                    name="firstName"
                    label={tGlobal('user.firstName')}
                    required
                    render={({ field }) => <Input {...field} />}
                  />
                </GridCol>

                <GridCol span={{ def: 1, sm: 6 }}>
                  <FormField
                    name="lastName"
                    label={tGlobal('user.lastName')}
                    required
                    render={({ field }) => <Input {...field} />}
                  />
                </GridCol>
                {!edit && (
                  <GridCol span={{ def: 1, sm: 6 }}>
                    <FormField
                      name="email"
                      label={tGlobal('user.email')}
                      required
                      render={({ field }) => <Input {...field} />}
                    />
                  </GridCol>
                )}
                <GridCol span={{ def: 1, sm: 6 }}>
                  <FormField
                    name="culture.language"
                    label={tGlobal('user.language')}
                    required
                    render={({ field }) => (
                      <Select {...field}>
                        <option value="" />
                        {LANGUAGES.map((x) => (
                          <option key={x.code} value={x.code}>
                            {x.name}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </GridCol>
                <GridCol span={{ def: 1, sm: 6 }}>
                  <FormField
                    name="culture.timezone"
                    label={tGlobal('user.timezone')}
                    required
                    render={({ field }) => (
                      <Select {...field}>
                        <option value="" />
                        {timeZonesNames.map((timezone, i) => (
                          <option key={`${timezone}-${i}`} value={timezone}>
                            {timezone}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </GridCol>
                {!hideAdvancedFields && (
                  <>
                    <GridCol span={{ def: 1, sm: 6 }}>
                      <FormField name="vatId" label={tGlobal('user.vatId')} render={({ field }) => <Input {...field} />} />
                    </GridCol>
                    <GridCol span={{ def: 1, sm: 6 }}>
                      <FormField
                        name="socialSecurityNumber.country.threeLetterCode"
                        label={tGlobal('user.country')}
                        render={({ field }) => (
                          <Select {...field}>
                            <option value="" />
                            {countriesSorted.map((country) => (
                              <option key={country.alpha3} value={country.alpha3}>
                                {country.name}
                              </option>
                            ))}
                          </Select>
                        )}
                      />
                    </GridCol>
                    <GridCol span={{ def: 1, sm: 6 }}>
                      <FormField
                        name="socialSecurityNumber.value"
                        label={tGlobal('user.socialSecurityNumber')}
                        render={({ field }) => <Input {...field} />}
                      />
                    </GridCol>
                    <GridCol span={{ def: 1, sm: 3 }}>
                      <FormField
                        name="source.integration"
                        label={tGlobal('legalPerson.invoicingAccount.integration')}
                        render={({ field }) => (
                          <Select {...field}>
                            <option value="" />
                            {INVOICING_INTEGRATIONS.map((type) => (
                              <option key={type} value={type}>
                                {tType(`InvoicingIntegration.${type}`)}
                              </option>
                            ))}
                          </Select>
                        )}
                      />
                    </GridCol>
                    <GridCol span={{ def: 1, sm: 3 }}>
                      <FormField
                        name="source.identifier"
                        label={tGlobal('legalPerson.invoicingAccount.identifier')}
                        render={({ field }) => <Input {...field} />}
                      />
                    </GridCol>
                  </>
                )}
              </GridRow>
            </article>
          </ModalContent>

          <ModalFooter>
            <FormSubmitButton type="submit">{tCommon('save')}</FormSubmitButton>
            <Button color="primary-dark" onClick={onClose}>
              {tCommon('close')}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
}
